import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import {
  PiHouseLight,
  PiShoppingCartLight,
  PiUsersLight,
} from "react-icons/pi";

import DashboardLayout from "lib/components/layouts/dashboard";
import SimpleCard from "lib/components/common/simple-card";
import SimpleTabs from "lib/components/common/simple-tabs";
import SimpleModal from "lib/components/common/simple-modal";
import SimpleDataTable from "lib/components/common/simple-data-table";
import SimpleButton from "lib/components/common/simple-button";

import AllPractitionerSubscriptions from "lib/components/super/practitioner-subscriptions";
import usePractitionerSubscriptions from "lib/hooks/usePractitionerSubscriptions";
import useRoomReservations from "lib/hooks/useRoomReservations";

import { PAGE_ROUTES } from "lib/constants/page-routes";
import { PRACTITIONER_ROOM_RESERVATIONS_COLUMNS } from "lib/constants/table-columns";
import { Row, Col } from "react-bootstrap";
import {
  areDatesEqual,
  formatDate,
  getDatePartWithoutTime,
  getDayDifference,
  getLocalISO,
  toDateFieldValue,
} from "lib/utils/date-utils";
import SimpleTextbox from "lib/components/common/simple-textbox";
import useFormBinder from "lib/hooks/useFormBinder";
import { toast } from "sonner";
import SimpleCalendar from "lib/components/common/calendar/simple-calendar";
import SimpleList from "lib/components/common/simple-list";
import SimpleAlert from "lib/components/common/simple-alert";

const breadcrumbItems = [
  {
    label: "Super Admin",
    path: PAGE_ROUTES.ADMIN.Dashboard,
  },
  {
    label: "App Management",
    path: PAGE_ROUTES.ADMIN.RoomsManagement,
  },
] as BreadcrumbItem[];

const PRACTITIONER_MANAGEMENT_TABS: GeneralItem[] = [
  {
    id: 1,
    label: "Practitioner Subscriptions",
    icon: <PiShoppingCartLight />,
  },
  {
    id: 2,
    label: "Room Allocations",
    icon: <PiHouseLight />,
  },
  {
    id: 3,
    label: "Patient Bookings",
    icon: <PiUsersLight />,
  },
];

const RoomsManagementPage = () => {
  const [selectedTab, setSelectedTab] = useState<number>(1);
  const [selectedSubscription, setSelectedSubscription] =
    useState<PractitionerSubscriptionDetails | null>(null);
  const [editingSubscription, setEditingSubscription] =
    useState<PractitionerSubscriptionDetails | null>(null);
  //const [editingFromDate, setEditingFromDate] = useState<string | null>(null);
  const { values, handleChange, setValues } =
    useFormBinder<UpdatePractitionerSubscription>({
      fromDate: toDateFieldValue(new Date().toISOString()),
      selectedDates: [],
    });
  const [roomReservations, setRoomReservations] = useState<RoomReservation[]>(
    []
  );

  const {
    subscriptions,
    getAllSubscriptions,
    updateSubscriptionDates,
    error: practitionerSubscriptionsError,
    loading: practitionerSubscriptionsLoading,
  } = usePractitionerSubscriptions();

  const {
    byPractitionerSubscription,
    loading: roomReservationLoading,
    error: roomReservationError,
  } = useRoomReservations();

  useEffect(() => {
    getAllSubscriptions();
  }, []);

  const handleSubscriptionSelection = (
    subscription: PractitionerSubscriptionDetails
  ) => {
    if (subscription?.isCancelled) {
      toast.error("This subscription is cancelled");
      return;
    }
    setSelectedSubscription(subscription);
    byPractitionerSubscription(subscription.id, setRoomReservations);
  };

  const handleConfirmEditFromDate = () => {
    if (!oldFromDate || !values.fromDate) return;

    updateSubscriptionDates(
      editingSubscription?.id,
      new Date(values.fromDate),
      (values.selectedDates ?? []).map((date) => getLocalISO(date)),
      () => getAllSubscriptions()
    );

    setEditingSubscription(null);
    setValues({ ...values, fromDate: editingSubscription?.fromDate });
  };

  const handleDateSelection = (date: Date) => {
    if (values.selectedDates.some((sDate) => areDatesEqual(sDate, date))) {
      setValues({
        ...values,
        selectedDates: values.selectedDates.filter(
          (sDate) => !areDatesEqual(sDate, date)
        ),
      });
    } else {
      setValues({
        ...values,
        selectedDates: [...values.selectedDates, getDatePartWithoutTime(date)],
      });
    }
  };

  /** Calculated vars */
  const oldFromDate = editingSubscription?.fromDate
    ? DateTime.fromISO(editingSubscription.fromDate).toJSDate()
    : null;

  // const newFromDate = editingFromDate
  //   ? DateTime.fromISO(editingFromDate).toJSDate()
  //   : null;

  const dayDifference = getDayDifference(
    oldFromDate,
    new Date(values.fromDate)
  );

  /** Calculated vars */

  console.log(roomReservations);

  return (
    <DashboardLayout breadcrumbItems={breadcrumbItems}>
      <SimpleCard
        title="App Management"
        isLoading={practitionerSubscriptionsLoading}
        error={practitionerSubscriptionsError}
      >
        <SimpleTabs
          tabs={PRACTITIONER_MANAGEMENT_TABS}
          selectedTabId={selectedTab}
          onTabChange={setSelectedTab}
        />
        {selectedTab === 1 && (
          <AllPractitionerSubscriptions
            subscriptions={subscriptions}
            onSubscriptionClick={handleSubscriptionSelection}
          />
        )}
        {selectedTab === 2 && <p>Tab 2</p>}
        {selectedTab === 3 && <p>Tab 3</p>}
      </SimpleCard>
      {/* Room Reservations Modal */}
      <SimpleModal
        title="Subscription Details"
        open={!!selectedSubscription}
        close={() => setSelectedSubscription(null)}
        loading={roomReservationLoading}
        error={roomReservationError}
        footer={
          <SimpleButton
            onClick={() => {
              if (!selectedSubscription?.fromDate) return;

              setValues({
                ...values,
                fromDate: toDateFieldValue(selectedSubscription.fromDate),
              });
              // setEditingFromDate(
              //   DateTime.fromISO(selectedSubscription.fromDate).toISODate()
              // );
              setEditingSubscription(selectedSubscription);
              setSelectedSubscription(null);
            }}
          >
            Edit
          </SimpleButton>
        }
      >
        <SimpleDataTable
          data={roomReservations}
          columns={PRACTITIONER_ROOM_RESERVATIONS_COLUMNS}
        />
      </SimpleModal>

      {/* Edit From Date Modal */}
      <SimpleModal
        title="Edit Dates"
        open={!!editingSubscription}
        close={() => {
          setEditingSubscription(null);
          //setEditingFromDate(null);
        }}
        footer={
          <SimpleButton
            disabled={values?.selectedDates?.length === 0}
            onClick={handleConfirmEditFromDate}
          >
            Confirm
          </SimpleButton>
        }
        //size="lg"
      >
        <Row>
          <Col sm="4">
            <SimpleAlert>
              <strong>From Database</strong>
            </SimpleAlert>
            <p>
              <label>From Date:</label>&nbsp;
              <strong>{formatDate(editingSubscription?.fromDate)}</strong>
            </p>
            <p>
              <label>To Date:</label>&nbsp;
              <strong>{formatDate(editingSubscription?.toDate)}</strong>
            </p>
            <p>
              <label>Total Days: </label>&nbsp;
              <strong>{(editingSubscription?.dates ?? []).length} days</strong>
            </p>
            <SimpleCalendar
              className="mt-3"
              selectedDates={(editingSubscription?.dates ?? []).map(
                (date) => new Date(date)
              )}
            />
          </Col>
          <Col sm="4">
            <SimpleAlert>
              <strong>Edit Here</strong>
            </SimpleAlert>
            <SimpleTextbox
              label="From Date:"
              name="fromDate"
              type="date"
              value={values.fromDate}
              onChange={handleChange}
            />
            <p>
              <label>To Date:</label>&nbsp;
              <strong>
                {formatDate(
                  values?.selectedDates?.[values?.selectedDates?.length - 1]
                )}
              </strong>
            </p>
            <p>
              <label>Total Days: </label>&nbsp;
              <strong>{(values?.selectedDates ?? []).length} days</strong>
            </p>
            <SimpleCalendar
              selectedDates={values.selectedDates}
              onChange={handleDateSelection}
            />
          </Col>
          <Col sm="4">
            <SimpleAlert>
              <strong>Edited Summary</strong>
            </SimpleAlert>
            <p>
              <label>From Date:</label>&nbsp;
              <strong>{formatDate(values?.fromDate)}</strong>
            </p>
            <p>
              <label>To Date:</label>&nbsp;
              <strong>
                {formatDate(
                  values?.selectedDates?.[values?.selectedDates?.length - 1]
                )}
              </strong>
            </p>
            <p>
              <label>Total Days: </label>&nbsp;
              <strong>{(values?.selectedDates ?? []).length} days</strong>
            </p>
            <SimpleList
              className="auto-scroll-350"
              items={(values?.selectedDates ?? []).map((date) =>
                formatDate(date)
              )}
            />
          </Col>
        </Row>
      </SimpleModal>
    </DashboardLayout>
  );
};

export default RoomsManagementPage;
